/**
 * Tiny math helpers used across the engine.
 */

export const TAU = Math.PI * 2;

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Smoothly approach `target` over time using a critically-damped spring. */
export function damp(current: number, target: number, lambda: number, dt: number): number {
  return lerp(current, target, 1 - Math.exp(-lambda * dt));
}

/** Wrap an angle to [-PI, PI]. */
export function wrapAngle(a: number): number {
  while (a > Math.PI) a -= TAU;
  while (a < -Math.PI) a += TAU;
  return a;
}

/** Damped angle interp that takes the short way around. */
export function dampAngle(current: number, target: number, lambda: number, dt: number): number {
  const delta = wrapAngle(target - current);
  return wrapAngle(current + delta * (1 - Math.exp(-lambda * dt)));
}

export function randRange(lo: number, hi: number): number {
  return lo + Math.random() * (hi - lo);
}

/** Deterministic 32-bit hash for procedural seeding. */
export function hash32(x: number): number {
  x = (x ^ 61) ^ (x >>> 16);
  x = x + (x << 3);
  x = x ^ (x >>> 4);
  x = Math.imul(x, 0x27d4eb2d);
  x = x ^ (x >>> 15);
  return x >>> 0;
}

/** Mulberry32 PRNG seeded by an integer. Deterministic, fast. */
export function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return function () {
    t |= 0;
    t = (t + 0x6d2b79f5) | 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/** 2D value noise for terrain. */
export function valueNoise2D(seed: number) {
  const rand = mulberry32(seed);
  const grid = new Float32Array(257 * 257);
  for (let i = 0; i < grid.length; i++) grid[i] = rand();

  function at(x: number, y: number): number {
    const ix = ((x | 0) % 256 + 256) % 256;
    const iy = ((y | 0) % 256 + 256) % 256;
    return grid[iy * 257 + ix];
  }

  return function noise(x: number, y: number): number {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const xf = x - xi;
    const yf = y - yi;
    const u = xf * xf * (3 - 2 * xf);
    const v = yf * yf * (3 - 2 * yf);
    const a = at(xi, yi);
    const b = at(xi + 1, yi);
    const c = at(xi, yi + 1);
    const d = at(xi + 1, yi + 1);
    return lerp(lerp(a, b, u), lerp(c, d, u), v);
  };
}

/** Multi-octave fractal Brownian motion built from value noise. */
export function fbm2D(seed: number, octaves = 4) {
  const noise = valueNoise2D(seed);
  return function (x: number, y: number): number {
    let amp = 1;
    let freq = 1;
    let sum = 0;
    let norm = 0;
    for (let i = 0; i < octaves; i++) {
      sum += noise(x * freq, y * freq) * amp;
      norm += amp;
      amp *= 0.5;
      freq *= 2;
    }
    return sum / norm;
  };
}
